import { useCallback, useEffect, useRef, useState } from "react";
import { HubSpotSuggestion } from "./interface";
import { getData, postData, deleteData } from "./backend";

function AppSuggestions() {
  const query = new URLSearchParams(window.location.search);
  const token = query.get('token')!;
  const autoGenerate = query.get('auto') === 'true';

  const [suggestion, setSuggestion] = useState<HubSpotSuggestion>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [actionLoading, setActionLoading] = useState<"send" | "reject" | null>(null);
  const [generateLoading, setGenerateLoading] = useState(false);

  useEffect(() => {
    if (!token) return;

    getData(`/hubspot/app/suggestions/`, token)
      .then((data: HubSpotSuggestion) => setSuggestion(data))
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [token]);

  const handleGenerate = useCallback(() => {
    setGenerateLoading(true);
    setError(undefined);

    postData(`/hubspot/app/suggestions/`, token)
      .then((data: HubSpotSuggestion) => setSuggestion(data))
      .catch(setError)
      .finally(() => setGenerateLoading(false));
  }, [token]);

  const autoGenerateRef = useRef(false);
  useEffect(() => {
    if (!autoGenerate || isLoading || suggestion || autoGenerateRef.current) return;
    autoGenerateRef.current = true;
    handleGenerate();
  }, [autoGenerate, isLoading, suggestion, handleGenerate]);

  if (isLoading || (autoGenerate && generateLoading)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-2">
        <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className="w-32 h-32" />
        <div className="text-gray-400 font-semibold text-center text-sm mt-2">{generateLoading ? "Generating suggestion..." : "Loading..."}</div>
      </div>
    );
  }

  if (!suggestion) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-6">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <img src="https://static.portant.co/logo-mark-blue.svg" alt="Portant" className="w-12 h-12" />
          <div className="text-xl font-bold text-gray-900 font-gilroy">No suggestion yet</div>
          <div className="text-sm text-gray-500">
            Generate a new draft email suggestion based on this record's context.
          </div>
          {error && (
            <div className="text-xs text-red-500 h-[320px] w-[600px] bg-gray-200 rounded-sm overflow-y-auto p-2" dangerouslySetInnerHTML={{ __html: error.message }}></div>
          )}
          <button
            onClick={handleGenerate}
            disabled={generateLoading}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generateLoading ? "Generating..." : "Generate suggestion"}
          </button>
        </div>
      </div>
    );
  }

  const sources = suggestion.sources ? suggestion.sources.split(",").map(s => s.trim()).filter(Boolean) : [];

  const handleSend = () => {
    setActionLoading("send");
    setError(undefined);
    postData(`/hubspot/app/suggestions/${suggestion.id}/send/`, token)
      .then((data: HubSpotSuggestion) => setSuggestion(data))
      .catch(setError)
      .finally(() => setActionLoading(null));
  };

  const handleReject = () => {
    setActionLoading("reject");
    setError(undefined);
    deleteData(`/hubspot/app/suggestions/${suggestion.id}/`, token)
      .then((data: HubSpotSuggestion) => setSuggestion(data))
      .catch(setError)
      .finally(() => setActionLoading(null));
  };

  return (
    <div className="flex h-screen">
      <div className="flex-1 flex flex-col p-6" />

      <div className="w-72 h-full border-l bg-gray-50 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto p-5 flex flex-col gap-5">
          <div>
            <div className="text-lg font-bold text-gray-900 font-gilroy">{suggestion.title}</div>
            <div className="text-xs text-gray-400 mt-1">{suggestion.previewSubtitle}</div>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Why this draft was prepared</div>
            <div className="text-sm text-gray-600 leading-relaxed">{suggestion.reason}</div>
          </div>

          {suggestion.insight && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Portant AI insight</div>
              <div className="text-sm text-gray-600 leading-relaxed bg-indigo-50 rounded-lg">{suggestion.insight}</div>
            </div>
          )}

          {sources.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sources used</div>
              <div className="flex flex-wrap gap-1.5 -mx-2">
                {sources.map((source, i) => (
                  <span key={i} className="px-2.5 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-full">
                    {source}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 p-5 border-t">
          {error && (
            <div className="text-xs text-red-500 text-center">{error.message}</div>
          )}
          {suggestion.status === "PENDING" ? (
            <>
              <button
                onClick={handleSend}
                disabled={!!actionLoading}
                className="w-full py-2.5 text-sm font-semibold text-white bg-blue rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === "send" ? "Sending..." : "Send email"}
              </button>
              <button
                onClick={handleReject}
                disabled={!!actionLoading}
                className="w-full py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === "reject" ? "Discarding..." : "Discard draft"}
              </button>
            </>
          ) : (
            <div className={`text-xs text-center font-semibold ${suggestion.status === "REJECTED" ? "text-amber" : suggestion.status === "COMPLETED" ? "text-green" : "text-gray-400"}`}>
              This suggestion has been {suggestion.status.toLowerCase()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AppSuggestions;
